import { useCallback, useMemo } from 'react';
import { useCurrentUserApi } from '@hero/api-hooks';
import {
  hasAnyCarrierRole,
  hasCustomerRole,
  isBrokerOnly as rolesIsBrokerOnly,
  isBusinessCustomerOnly as rolesIsBusinessCustomerOnly,
  isCarrierCompanyOnly as rolesIsCarrierCompanyOnly,
  isCarrierSideOnly,
  isCustomerOnly as rolesIsCustomerOnly,
  isIndividualCustomerOnly as rolesIsIndividualCustomerOnly,
} from '@hero/core';
import { isIdMatch } from '@hero/schema';
import type { PartyRole, PartyTarget, User } from '@hero/schema';
import { resolveDriverSession } from './resolveDriverSession';

type CurrentOrg = User['orgs'][number];

/**
 * Returns the current user's identity with derived convenience fields.
 *
 * Single source of truth for current-user reads across every app
 * (dashboard, admin-panel, carrier-app, customer-app). Wraps
 * `useCurrentUserApi` (`api.auth.me()` via `ApiProvider`) so
 * persistence + invalidation flow through
 * `ENTITY_CONFIGS.currentUser` (`staleTime: 10m`, `gcTime: 24h`,
 * `persist: true`, `refetchOnWindowFocus: true`) on every platform.
 *
 * Auth-v2 unified shape: the getMe response is `{ user, org?, driver? }`.
 * `org` is the full unified `Org` doc for the currently-active org
 * (settings, fleet, members, …) — use it for deep reads like
 * `org.settings`. `currentOrg` is the lightweight `user.orgs[i]` Party
 * snapshot (`_id`, `name`, `target`, `roles`, `memberType`) — always
 * available even before the full Org doc loads.
 *
 * Role helpers are derived from the relations-v2 `PartyRole[]` arrays:
 *
 * - **`orgRoles`** = the active org's `roles[]` (full doc first, Party
 *   snapshot as fallback). Used by the carrier-side / customer-side
 *   classification helpers below (`isAnyCarrier`, `isCustomerOnly`,
 *   `isBusinessCustomerOnly`, …).
 *
 * - **`userRoles`** = `user.roles[]` — network identity the user
 *   holds globally (`driver`, `individual_customer`, `contact`, …).
 *
 * - **`isDriver` / `isFreelancerDriver` / `isCarrierCompanyDriver`**
 *   are **session-scoped** via {@link resolveDriverSession}: when
 *   signed into an Org they follow `memberType`; with no active org
 *   they fall back to `user.roles`. Consumers must not re-derive
 *   these from `user.roles.includes('driver')`.
 */
export const useCurrentUser = () => {
  const query = useCurrentUserApi();
  const { user, org, driver } = query.data ?? {};

  const orgs = user?.orgs ?? [];
  const currentOrg: CurrentOrg | undefined = useMemo(() => {
    if (!user) return undefined;
    const matched = user.currentSignedInOrgId
      ? orgs.find(o => isIdMatch(user.currentSignedInOrgId, o._id))
      : undefined;
    return matched ?? orgs[0];
  }, [user, orgs]);

  // Roles of the currently-active org. Prefer the full Org doc from
  // the getMe response; fall back to the `user.orgs[i]` Party snapshot
  // so role helpers work even when the full doc hasn't loaded.
  const orgRoles: PartyRole[] = (org?.roles ?? currentOrg?.roles ?? []) as PartyRole[];

  const {
    userRoles,
    memberType,
    hasActiveOrgSession,
    isDriver,
    isFreelancerDriver,
    isCarrierCompanyDriver,
  } = useMemo(() => resolveDriverSession(user), [user]);

  const getOrgsByTarget = useCallback(
    (targets: PartyTarget[]): CurrentOrg[] => orgs.filter(o => targets.includes(o.target)),
    [orgs],
  );

  return {
    user,
    /**
     * Full unified `Org` doc for the user's currently-active org
     * (`user.currentSignedInOrgId`), if any. Replaces the legacy
     * `business` / `carrier` split — read `org.roles` for
     * role-specific handling, `org.settings` / `org.fleet` /
     * `org.members` for deep data.
     */
    org,
    driver,
    isLoggedIn: !!query.data?.user,

    name: user?.name ?? '',
    phone: user?.phone ?? '',
    orgName: org?.name ?? currentOrg?.name,
    orgId: (org?._id ?? currentOrg?._id) as string | undefined,

    /**
     * The user's org snapshot (`user.orgs[i]`) that matches
     * `currentSignedInOrgId`, falling back to the first entry in
     * `user.orgs`. `undefined` when the user has no orgs (e.g. a
     * freelancer driver pre-signup). Prefer `memberType` /
     * `hasActiveOrgSession` for permission gates — those ignore the
     * `orgs[0]` fallback.
     */
    currentOrg,

    /**
     * Filter the user's orgs by `PartyTarget`. Replaces mobile's
     * legacy `useUserInfo(user).getOrgsByTarget` so the shared hook
     * is a strict superset.
     */
    getOrgsByTarget,

    // ── Org-level role helpers (PartyRole[] from the active org) ──────
    /**
     * `roles[]` of the currently-active org (full `Org` doc first,
     * `user.orgs[i]` snapshot as fallback). Empty array when the user
     * has no Org (e.g. a freelancer driver, individual customer).
     */
    orgRoles,
    /** `true` if any org role is carrier-side (`carrier_company` or `broker`). */
    isAnyCarrier: hasAnyCarrierRole(orgRoles),
    /** `true` if any org role is customer-side (`business_customer` or `individual_customer`). */
    isAnyCustomer: hasCustomerRole(orgRoles),
    /** `true` if every org role is customer-side (no carrier / broker). */
    isCustomerOnly: rolesIsCustomerOnly(orgRoles),
    /** `true` if every org role is carrier-side (no customer). */
    isCarrierSideOnly: isCarrierSideOnly(orgRoles),
    /** `true` if the only org role is `carrier_company`. */
    isCarrierCompanyOnly: rolesIsCarrierCompanyOnly(orgRoles),
    /** `true` if the only org role is `broker`. */
    isBrokerOnly: rolesIsBrokerOnly(orgRoles),
    /** `true` if the only org role is `business_customer`. */
    isBusinessCustomerOnly: rolesIsBusinessCustomerOnly(orgRoles),
    /** `true` if the only org role is `individual_customer`. */
    isIndividualCustomerOnly: rolesIsIndividualCustomerOnly(orgRoles),

    // ── Session-scoped membership / driver helpers ───────────────────
    /**
     * The user's own `PartyRole[]` (relations-v2). Network-level
     * identity only — do not use for "am I a driver in this session?"
     * gates; use {@link isDriver} instead.
     */
    userRoles,
    /**
     * In-org `memberType` for the active org session (`admin` /
     * `dispatcher` / `driver` / `employee`). `undefined` when there
     * is no active org session.
     */
    memberType,
    /** True when `user.currentSignedInOrgId` resolves to an org row. */
    hasActiveOrgSession,
    /**
     * Session-scoped driver: `memberType === 'driver'` when signed
     * into an Org; otherwise `user.roles` includes `driver`.
     */
    isDriver,
    /** Driver with no active org session (owner-operator / individual). */
    isFreelancerDriver,
    /** Driver membership inside the currently signed-in Org. */
    isCarrierCompanyDriver,
    /** `true` if the user's role list includes `individual_customer`. */
    isIndividualCustomer: userRoles.includes('individual_customer'),

    isLoading: query.isLoading,
    isError: query.isError,
  };
};
